'use client'

/**
 * The fixed slot under the navbar that holds the site-wide sub-banners.
 *
 * 🚨 IT EXISTS TO OWN THE HORIZONTAL GEOMETRY. The slot used to be `fixed inset-x-0 top-16` in
 * layout.tsx — the full viewport width — while each banner centred its own `max-w-[1200px]` box on
 * that width. The left sidebar (72-368px) and the chat dock both paint over it, so the banner was
 * centred on the wrong axis and its CTA / dismiss button slid underneath the dock at ordinary
 * window sizes. It only looked survivable at 1920px, which is where it was built.
 *
 * The slot now spans exactly the content band: left = the live sidebar width (desktop only, the
 * sidebar is a drawer below md), right = `--alerts-chat-right-pad`, the same var ConditionalMain
 * and /alerts already use to reserve room for the dock. One source of truth for that edge.
 *
 * `data-sub-banner` is load-bearing: ConditionalMain measures this element and pads the page by its
 * height. Keep the attribute on the OUTER node.
 */

import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useSidebar } from '@/contexts/SidebarContext'

export default function SubBannerSlot({ children }: { children: ReactNode }) {
  const { width: sidebarWidth } = useSidebar()
  const [isDesktop, setIsDesktop] = useState(false)

  // Same 768px breakpoint ConditionalMain uses for its margin-left, so the two can't disagree
  // about where the content band starts.
  useEffect(() => {
    const update = () => setIsDesktop(window.innerWidth >= 768)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return (
    <div
      data-sub-banner=""
      // pointer-events-none on the slot + auto on each banner: the empty gutter above the page
      // never eats clicks meant for the content underneath.
      // ⚠ `top` follows the header rather than being a hard top-16, for the same reason the header
      // is no longer top-0: the alert banner stacks above both. ConditionalMain owns the var.
      className="pointer-events-none fixed z-20 transition-[left,right] duration-200 ease-out"
      style={{
        top: 'calc(4rem + var(--alert-banner-h, 0px))',
        left: isDesktop ? sidebarWidth : 0,
        right: 'var(--alerts-chat-right-pad, 0px)',
      }}
    >
      {children}
    </div>
  )
}
